// Everything the bulk cross-listing pages need to turn a run into what the seller sees: the
// words for its status, the words for one marketplace result, how far along it is, and the few
// facts about the run those depend on.
//
// The wording here is for sellers, not for developers: no batch, no extension, no platform keys.

import { accountLabel } from 'src/utils/account-labels';
import { getPlatformDisplayName } from 'src/utils/platform-label';
import { mapPlatformError } from 'src/utils/platform-error-message';
import { isUnconfirmedCreate, unconfirmedCreateReason } from 'src/utils/extension-publish-rules';

import { EXTENSION_PLATFORMS } from '../constants';
import { parsePlatformResultKey } from './platform-keys';

// ── How long the run has been going ─────────────────────────────────────────────────────────

/**
 * A duration in plain words, e.g. '45s', '2m 14s', '3h 7m'.
 * @param {number} milliseconds
 * @returns {string} the duration, or '' when there is no sensible one to show
 */
export function formatElapsed(milliseconds) {
  if (!Number.isFinite(milliseconds) || milliseconds < 0) return '';

  const totalSeconds = Math.floor(milliseconds / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

// ── The run as a whole ──────────────────────────────────────────────────────────────────────

const RUN_STATUS_DISPLAY = {
  queued: { label: 'Listing your items…', color: 'info', icon: 'solar:refresh-bold' },
  processing: { label: 'Listing your items…', color: 'info', icon: 'solar:refresh-bold' },
  creating_via_extension: {
    label: 'Waiting for your browser',
    color: 'info',
    icon: 'solar:clock-circle-bold',
  },
  needs_extension: {
    label: 'Needs the Voolist extension',
    color: 'warning',
    icon: 'solar:plug-circle-bold',
  },
  paused: { label: 'Paused', color: 'warning', icon: 'solar:pause-circle-bold' },
  stopped: {
    label: 'Stopped, some items weren’t listed',
    color: 'warning',
    icon: 'solar:stop-circle-bold',
  },
  completed: { label: 'Done', color: 'success', icon: 'solar:check-circle-bold' },
  completed_with_errors: {
    label: 'Done, some items need attention',
    color: 'warning',
    icon: 'solar:danger-triangle-bold',
  },
  not_found: { label: 'Results expired', color: 'default', icon: 'solar:clock-circle-bold' },
  expired: { label: 'Results expired', color: 'default', icon: 'solar:clock-circle-bold' },
};

/**
 * How the run's status reads on screen.
 *
 * @param {string} status - a run status from deriveRunStatus
 * @param {string} [currentPlatform] - the marketplace being created on right this second, when
 *   one is. Without it, work handed to the browser reads as waiting rather than as happening.
 * @returns {{label:string, color:string, icon:string}}
 */
export function describeRunStatus(status, currentPlatform) {
  const display = RUN_STATUS_DISPLAY[status] || RUN_STATUS_DISPLAY.queued;

  if (status === 'creating_via_extension' && currentPlatform) {
    return { ...display, label: `Listing on ${getPlatformDisplayName(currentPlatform)}…` };
  }

  return display;
}

// ── One marketplace result ──────────────────────────────────────────────────────────────────

/**
 * How one marketplace result reads on screen.
 *
 * @param {Object} platformResult - one entry of a listing's `results`
 * @param {Object} [options]
 * @param {boolean} [options.isCreating] - true while the browser is working through this run
 * @param {boolean} [options.isStopped] - the seller stopped this run, so nothing still owed to
 *   the browser is ever going to be created
 * @param {boolean} [options.extensionUnavailable] - the extension did not answer, so nothing still
 *   owed to the browser is being made right now
 * @param {boolean} [options.refusedAsAutomation] - this marketplace refused the run as automation,
 *   so the run stopped sending it anything and this item was never attempted
 * @param {string} [options.source] - where the run being shown came from. This is the one contract
 *   every view of a run shares, and the only thing that says whether work still owed to the browser
 *   is coming:
 *     'live'     — the run this browser is following right now; that work is on its way
 *     'server'   — an earlier run, as the server still remembers it
 *     'snapshot' — an earlier run, from what this browser saved of it
 *     'expired'  — the run is gone from both
 *   Only 'live' means anything still pendingExtension will be picked up: the extension only ever
 *   creates for the run this browser is following.
 * @returns {{state:string, label:string, color:string, icon:string, busy?:boolean}} `busy` when the row's work
 *   is being done right now, so it is drawn moving rather than as a still icon
 */
export function describePlatformRow(
  platformResult,
  {
    isCreating = false,
    isStopped = false,
    source = 'live',
    extensionUnavailable = false,
    refusedAsAutomation = false,
  } = {}
) {
  // Work the server handed to the browser is marked successful before anything is posted, so
  // this has to be answered before success is.
  if (platformResult?.pendingExtension) {
    if (isStopped) {
      return {
        state: 'notCreated',
        label: 'Not listed, you pressed Stop',
        color: 'text.secondary',
        icon: 'solar:minus-circle-outline',
      };
    }

    // Answered before "nobody is following this run", because it is the more specific reason and
    // it stays true afterwards: the marketplace refused us, and a later reload must not offer this
    // item as something the browser is about to pick up.
    //
    // Deliberately not 'failed'. Nothing was sent for this item, so nothing about it was refused
    // and there is nothing on the marketplace to check.
    if (refusedAsAutomation) {
      return {
        state: 'notCreated',
        label: 'Not listed, the marketplace asked you to add items yourself',
        color: 'text.secondary',
        icon: 'solar:minus-circle-outline',
      };
    }

    // Nobody is following this run — it is an earlier one being looked at, or the server has
    // forgotten it — so nothing still pendingExtension will ever be picked up.
    if (source !== 'live') {
      return {
        state: 'notCreated',
        label: 'Not listed yet, cross-list it again',
        color: 'text.secondary',
        icon: 'solar:minus-circle-outline',
      };
    }

    // "Waiting for your browser" is only true while there is a browser to wait for. With nothing
    // answering, it reads as patience for something that is not happening.
    if (extensionUnavailable) {
      return {
        state: 'needsExtension',
        label: 'Waiting for the Voolist extension',
        color: 'warning.main',
        icon: 'solar:plug-circle-bold',
      };
    }

    return isCreating
      ? { state: 'creating', label: 'Listing…', color: 'info.main', icon: 'solar:refresh-bold', busy: true }
      : {
        state: 'waiting',
        label: 'Waiting for your browser',
        color: 'info.main',
        icon: 'solar:clock-circle-bold',
      };
  }

  // Answered before failure, because these did not fail. The browser stopped talking to us before it
  // said what happened, or the marketplace never said whether it made the post — either way nobody
  // knows, and the seller has to look. Or the browser posted the item and could not save it here,
  // which needs nothing from them; or the post is already another of their listings, which they
  // merge. The words say which.
  if (isUnconfirmedCreate(platformResult)) {
    const labels = {
      linkedToAnotherListing: 'Listed, merge needed',
      unrecorded: 'Listed, not saved in Voolist yet',
    };
    return {
      state: 'unconfirmed',
      label: labels[unconfirmedCreateReason(platformResult)] || 'Didn’t finish',
      color: 'warning.main',
      icon: 'solar:clock-circle-bold',
    };
  }

  if (!platformResult?.success) {
    return { state: 'failed', label: 'Failed', color: 'error.main', icon: 'solar:close-circle-bold' };
  }

  // The account already held the item's post — a Whatnot listing hidden there, say — so the run updated
  // that post instead of posting the item twice. The update runs from the seller's browser, alongside the
  // run's own work: it moves while the run works, and rests once the run is done.
  if (platformResult.editQueued) {
    return {
      state: 'updateQueued',
      label: 'Already listed there, Voolist is updating it',
      color: 'success.main',
      icon: 'solar:check-circle-bold',
      busy: isCreating,
    };
  }

  // Nothing was posted, and rightly: the item sold out while the run waited, and the sale withdrew
  // the post that was waiting to go up. Nothing failed and nothing is left to do.
  if (platformResult.soldOut) {
    return {
      state: 'soldOut',
      label: 'Not listed, it sold out',
      color: 'text.secondary',
      icon: 'solar:minus-circle-outline',
    };
  }

  if (platformResult.alreadyCreated || platformResult.skipped) {
    return {
      state: 'alreadyListed',
      label: 'Already listed',
      color: 'success.main',
      icon: 'solar:check-circle-bold',
    };
  }

  return { state: 'listed', label: 'Listed', color: 'success.main', icon: 'solar:check-circle-bold' };
}

// What a seller can do about a row this page cannot try again. Either Voolist itself made that
// create — from a request the finished run no longer holds — or this browser is no longer the one
// following the run, and only the browser that owns a run can create for it. Either way there is
// nothing here to send again, and running the bulk cross-list for that item is the way to retry.
const RUN_AGAIN_FOR_IT = 'Open the item to fix this, then cross-list it again.';

/**
 * What to tell the seller to do about one failed marketplace.
 *
 * Advice whose way forward is to try the item again is written for a row that can be tried again
 * from this page. When this row has no Retry, that advice would point at a button that is not there,
 * so it is replaced with what actually helps. Advice whose way forward is something else — tell
 * Voolist, finish the item on the marketplace — does not point at a Retry button at all, is just as
 * true on a row without one, and is kept: replacing it would send the seller to fix an item that has
 * nothing wrong with it.
 *
 * @param {boolean} canRetryHere - whether this row really offers a Retry: the same answer the
 *   button itself is drawn from, so the words and the button can never disagree
 * @param {{suggestion?: string, wayForward?: string}} [problem] - what the error mapper said about
 *   this failure. Advice that does not say what its way forward is is read as a retry, so it is
 *   replaced rather than left pointing at nothing.
 * @returns {string}
 */
export function failureSuggestion(canRetryHere, { suggestion, wayForward = 'retry' } = {}) {
  return canRetryHere || wayForward !== 'retry' ? suggestion : RUN_AGAIN_FOR_IT;
}

/**
 * What one failed marketplace row shows: the failure in plain words, whether the row offers a
 * Retry, and what to tell the seller to do.
 *
 * @param {Object} platformResult - one entry of a listing's `results`
 * @param {string} platform
 * @param {Object} options
 * @param {boolean} options.browserCreatesThis - this browser is the one that creates on this
 *   marketplace for the run on screen, so a Retry here could send the create again
 * @returns {{problem: Object, canRetryHere: boolean, suggestion: string}} `problem` is what
 *   mapPlatformError said about the failure
 */
export function describeFailedRow(platformResult, platform, { browserCreatesThis }) {
  const problem = mapPlatformError(platformResult?.error, platform);
  // One answer drives both the button and the words, so they can never disagree: this row can be
  // tried again only when this browser is the one that creates for it AND the failure is the kind
  // retrying could fix.
  const canRetryHere = browserCreatesThis && problem.retryable !== false;
  return { problem, canRetryHere, suggestion: failureSuggestion(canRetryHere, problem) };
}

/**
 * What to tell the seller about a marketplace whose post may be live.
 *
 * Never offers a retry, and says so plainly enough that the seller does not go looking for one.
 * Posting the item again is the one thing that would genuinely hurt here, so the way back is
 * always to look at the marketplace first.
 *
 * @param {Object} platformResult - one entry of a listing's `results`
 * @param {string} marketplaceName - what the seller calls this shop or marketplace
 * @returns {{message:string, action:string}}
 */
export function describeUnconfirmedRow(platformResult, marketplaceName) {
  const where = marketplaceName || 'the marketplace';
  const reason = unconfirmedCreateReason(platformResult);

  if (reason === 'linkedToAnotherListing') {
    return {
      message: `Your item is on ${where}, and that post is already in Voolist as another listing.`,
      action: 'Merge the two to keep one.',
    };
  }

  if (reason === 'unrecorded') {
    return {
      message: `Your item is on ${where}, but Voolist hasn’t linked it yet. Until it does, your edits won’t reach it and it won’t come down by itself when the item sells.`,
      action: 'We’ll keep trying, so there’s nothing for you to do right now.',
    };
  }

  // The other two reasons both leave nobody knowing whether the item went up, so the seller does
  // the same thing about either. Only the cause they are told differs.
  const lookBeforePostingAgain = `Open ${where} and look for it. If it isn’t there, open the item and click Save Changes to post it.`;

  if (reason === 'marketplaceUnconfirmed') {
    return {
      message: `${marketplaceName || 'The marketplace'} had a problem and didn’t confirm whether this item went up.`,
      action: lookBeforePostingAgain,
    };
  }

  return {
    message: `Your browser stopped responding, so we can’t tell whether this item went up on ${where}.`,
    action: lookBeforePostingAgain,
  };
}

/**
 * True when every marketplace this item was meant to go to is done with — created now, or already
 * there. An item with no marketplace results at all is one whose whole job failed before any
 * marketplace was tried, so it is never done.
 */
function itemIsDone(row) {
  const marketplaces = Object.values(row?.results || {});
  return (
    marketplaces.length > 0 &&
    marketplaces.every((result) => result.success && !result.pendingExtension)
  );
}

/**
 * A finished run's items split into the two things a seller reads a finished run for: what still
 * wants them, and what is done. A marketplace that failed, and one the run never got to, both
 * leave the item in the first group — nothing went out there either way.
 *
 * Each group keeps the run's own order, so an item stays where the seller last saw it.
 *
 * @param {Array<Object>} mergedResults - the run's items, from mergeExtensionResults
 * @returns {{needAttention: Array<Object>, listed: Array<Object>}}
 */
export function groupSettledResults(mergedResults) {
  const needAttention = [];
  const listed = [];

  (mergedResults || []).forEach((row) => {
    if (itemIsDone(row)) listed.push(row);
    else needAttention.push(row);
  });

  return { needAttention, listed };
}

/**
 * What to call one marketplace result: the seller's own name for the shop it went to when the
 * result belongs to a specific account, otherwise the marketplace's name.
 *
 * @param {string} resultKey - 'poshmark' or 'ebay:370'
 * @param {Object} platformResult - one entry of a listing's `results`
 * @param {Array<Object>} marketplaces - the seller's connected shops
 * @returns {string}
 */
export function platformRowLabel(resultKey, platformResult, marketplaces) {
  if (platformResult?.accountLabel) return platformResult.accountLabel;

  const { platform, integrationId } = parsePlatformResultKey(resultKey);
  return integrationId === null
    ? getPlatformDisplayName(platform)
    : accountLabel(marketplaces, platform, integrationId);
}

// ── Numbers the page shows ──────────────────────────────────────────────────────────────────

function clampPercent(value) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

/**
 * How far along the whole run is, as a percentage.
 *
 * While the server is still working through the items, that is the server's own count of items
 * done. Once it has finished, everything left is a marketplace post the browser still owes.
 *
 * @param {Object} serverStatus - the run's status payload from the server
 * @param {{totalSuccess?:number, totalFailed?:number, totalUnconfirmed?:number,
 *   totalPending?:number}} counts
 * @returns {number} 0-100
 */
export function runProgressPercent(serverStatus, counts) {
  if (serverStatus?.status !== 'completed') return clampPercent(serverStatus?.progress || 0);

  const { totalSuccess = 0, totalFailed = 0, totalUnconfirmed = 0, totalPending = 0 } = counts || {};
  // A marketplace left unconfirmed is finished as far as the run is concerned: nothing more will
  // happen to it on its own. Leaving it out of the total would hold the bar short of the end for
  // work that is never coming.
  const settled = totalSuccess + totalFailed + totalUnconfirmed;
  const all = settled + totalPending;

  return all === 0 ? 100 : clampPercent((settled / all) * 100);
}

/**
 * True when any marketplace in this run is created through the seller's browser, which is what
 * makes "waiting on browser" worth showing at all.
 * @param {Array<Object>} mergedResults
 */
export function hasExtensionWork(mergedResults) {
  return (mergedResults || []).some((row) =>
    Object.keys(row.results || {}).some((resultKey) =>
      EXTENSION_PLATFORMS.includes(parsePlatformResultKey(resultKey).platform)
    )
  );
}

